import { CheckIcon, CheckSuccessfulIcon } from "@/components/Icons";
import { TonIcon } from "@/components/Icons";
import { Avatar } from "@/components/Icons/Avatar";
import { TonUsdPill } from "@/components/TonUsdPill";
import { useUserContext } from "@/contexts/UserContext";
import type { EntryResult } from "@roman/shared/types/Result";
import { cn } from "@roman/shared/utils";
import { formatNanoTon } from "@roman/shared/utils/formatNanoTon";
import { useState } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";

interface UserTabProps {
  entries: EntryResult[];
  onEntryClick: (entry: EntryResult | null) => void;
  playerImageMap: Record<string, string | null>;
}

export const UserTable = ({ entries, onEntryClick, playerImageMap }: UserTabProps) => {
  const [isOnlyYourTeam, setIsOnlyYourTeam] = useState(false);
  const user = useUserContext();

  const maxFantasyPoint = Math.max(...entries.map((user) => user.fantasyPoint));
  const displayedEntries = isOnlyYourTeam ? entries.filter((entry) => entry.userId === user.id) : entries;

  return (
    <div className="pb-5 h-full flex flex-col">
      <div className="flex w-full justify-between items-center py-4">
        <div className="flex gap-1 hover:cursor-pointer" onClick={() => setIsOnlyYourTeam(!isOnlyYourTeam)}>
          {isOnlyYourTeam ? <CheckSuccessfulIcon className="w-4 h-4" /> : <CheckIcon className="w-4 h-4" />}
          <p className="text-xs text-icon">Show only your team</p>
        </div>
        <TonUsdPill />
      </div>

      <div className="flex flex-col w-full gap-1 flex-1 ">
        <AutoSizer>
          {({ height, width }) => (
            <List height={height} itemCount={displayedEntries.length} itemSize={112} width={width}>
              {({ index, style }: { index: number; style: React.CSSProperties }) => {
                const entry = displayedEntries.sort((a, b) => a.rank - b.rank)[index];
                return (
                  <div style={style} key={entry.id}>
                    <EntryRow
                      entry={entry}
                      maxFantasyPoint={maxFantasyPoint}
                      playerImageMap={playerImageMap}
                      onEntryClick={onEntryClick}
                      isYourEntry={entry.userId === user.id}
                    />
                  </div>
                );
              }}
            </List>
          )}
        </AutoSizer>
      </div>
    </div>
  );
};
export const setRankColor = (rank: number) => {
  switch (rank) {
    case 1:
      return "bg-gradient-to-b from-[#FFE500] to-[#3D4700] bg-clip-text text-transparent text-base font-bold";
    case 2:
      return "bg-gradient-to-b from-[#FFFFFF] to-[#414141] bg-clip-text text-transparent text-base font-bold";
    case 3:
      return "bg-gradient-to-b from-[#CBA080] to-[#693F0D] bg-clip-text text-transparent text-base font-bold";
    default:
      return "text-xs";
  }
};

const EntryRow = ({
  entry,
  maxFantasyPoint,
  playerImageMap,
  onEntryClick,
  isYourEntry,
}: {
  entry: EntryResult;
  maxFantasyPoint: number;
  playerImageMap: Record<string, string | null>;
  onEntryClick: (entry: EntryResult) => void;
  isYourEntry: boolean;
}) => {
  const { fantasyPoint, rank, userName, prize } = entry;

  const width = (fantasyPoint / maxFantasyPoint) * 130; // 130px is the max width

  const isCaptain = (playerId: string): boolean => {
    return entry.selectedCaptains.captainPlayerId === playerId;
  };

  const isViceCaptain = (playerId: string): boolean => {
    if (entry.selectedCaptains.type === "0002") {
      return entry.selectedCaptains.viceCaptainPlayerId === playerId;
    }
    return false;
  };

  return (
    <div
      className={cn(
        "flex justify-center items-between text-sm py-2.5 space-x-[5px] px-2",
        isYourEntry && "bg-gradient-to-r from-[#2A2A2A] to-[#2A2A2A]/0",
      )}
      onClick={() => onEntryClick(entry)}
    >
      <div className="flex items-center w-[77%]">
        <p className={cn("flex-none", setRankColor(rank))}>{rank}</p>
        <div className="px-2 pr-1 flex-none">
          <Avatar photoUrl={entry.userPhotoUrl} />
        </div>

        <div className="flex flex-col gap-2 grow overflow-hidden">
          {isYourEntry ? (
            <div className="flex gap-1 items-center">
              <CheckSuccessfulIcon className="w-4 h-4" />
              <p className="text-sm">{userName}</p>
            </div>
          ) : (
            <p className="text-sm">{userName}</p>
          )}
          <div
            className="h-0.5 bg-disabled"
            style={{ width: `${width}px`, boxShadow: rank === 1 ? "0px 0px 9px 0px #FF8A00" : "none" }}
          />
          <div className="flex px-1.5 pt-1.5 justify-start space-x-[7px] flex-1 overflow-x-scroll w-full hide-scrollbar ">
            {entry.selectedPlayers.map((playerId: string) => (
              <div
                className="p-0.5 rounded-md"
                style={{
                  padding: "1px",
                  backgroundImage:
                    isCaptain(playerId) || isViceCaptain(playerId)
                      ? "linear-gradient(to bottom right, #FFE500, #00E218)"
                      : "none",
                  WebkitBackgroundClip: "padding-box",
                  backgroundClip: "padding-box",
                  overflow: "visible",
                }}
                key={playerId}
              >
                <div className="relative bg-chicago-700 rounded-md w-7 h-[42px]">
                  {(isCaptain(playerId) || isViceCaptain(playerId)) && (
                    <div
                      className="absolute top-0 left-0 text-xs font-bold text-black rounded-full z-10 flex items-center justify-center"
                      style={{
                        width: "1.75rem",
                        backgroundImage: "linear-gradient(to bottom right, #FFE500, #00E218)",
                        transform: "translate(-25%, -35%)",
                      }}
                    >
                      {isCaptain(playerId) ? "C" : "VC"}
                    </div>
                  )}
                  <img
                    key={playerId}
                    src={playerImageMap[playerId] || "/temp/player1.png"}
                    alt="player"
                    className="rounded-md select-none cursor-pointer w-7 h-[42px]"
                    draggable={false}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-end justify-center gap-1">
        <div className="text-right">{fantasyPoint.toFixed(2)}pt</div>
        <div className="flex items-center gap-1">
          <TonIcon className="w-4 h-4" />
          <span className="text-center">{formatNanoTon(prize, 4)}</span>
        </div>
      </div>
    </div>
  );
};
